import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { KLING2_5_PRO_RESOLUTION_TO_WIDTH_HEIGHT_MAP } from "../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const kling25ProParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16", "1:1"]).default("9:16"),
    duration: z.enum(["5", "10"]).default("5"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const kling25Pro = defineModel({
  key: "kling2_5-pro",
  stepType: "video",
  spec: {
    label: "Kling 2.5 Turbo Pro",
    description: "Cheap video model with good scene control",
    icon: "kling-icon.svg",
    tags: ["video-gen"],
    featured: true,
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: {
      aspectRatio: true,
      duration: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "First Frame",
          slot: "first_frame",
          required: false,
        },
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Last Frame",
          slot: "last_frame",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "9:16", value: "9:16" },
          { label: "16:9", value: "16:9" },
          { label: "1:1", value: "1:1" },
        ],
      },
      duration: {
        items: [
          { label: "5s", value: "5" },
          { label: "10s", value: "10" },
        ],
      },
    },
    params: kling25ProParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/kling-video/v2.5/pro",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 70_000 }],
  },
  providers: [
    {
      provider: "runware",
      modelId: "klingai:6@1",
      estimateCost(params) {
        return 0.07 * 1_000_000 * Number(params.duration) * (1 + BRAND_MARKUP);
      },
      costPerSecond: 0.07 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const size =
          KLING2_5_PRO_RESOLUTION_TO_WIDTH_HEIGHT_MAP[params.aspectRatio];

        const firstFrame = params.attachments?.find(
          (attachment) => attachment.slot === "first_frame",
        )?.data;

        const lastFrame = params.attachments?.find(
          (attachment) => attachment.slot === "last_frame",
        )?.data;

        if (params.prompt?.length === 0) {
          throw new ValidationError(
            "Please enter a prompt to generate a video.",
          );
        }

        const inputFrameImages = [
          ...(firstFrame ? [firstFrame] : []),
          ...(lastFrame ? [lastFrame] : []),
        ];

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          duration: Number(params.duration),
          ...(inputFrameImages.length > 0
            ? { inputs: { frameImages: inputFrameImages } }
            : { width: size.width, height: size.height }),
        };
      },
    },
  ],
});
